import type {
  CustomStatus, ManualState, PresenceSnapshot, PresenceWire, ResetAfter, SelfDeclaration,
} from './types';
import { activityFromWire, fromWire } from './presenceResolver';

/** Wire shape of `GET /api/v1/presence?userIds=` (one row of cyphex.user_presence). */
export interface PresenceDto {
  userId: string;
  autoState: PresenceWire;
  /** `user_presence.activity_state` — null is the normal case. */
  activityState: PresenceWire | null;
  manualState: PresenceWire | null;
  manualExpiresAt: string | null;
  effectiveState: PresenceWire;
  stateSince: string;
  lastActivityAt: string | null;
  connectionCount: number;
  statusEmoji: string | null;
  statusText: string | null;
  statusExpiresAt: string | null;
  statusPinInChat: boolean;
}

export interface PresenceApi {
  /**
   * GET /api/v1/presence/me — what the signed-in user last DECLARED.
   *
   * Nothing used to call this, so every reload started from the seeded
   * placeholder: the avatar chip said "Available" with no custom status even
   * for a user who had set Do not disturb and a status message minutes earlier.
   */
  fetchSelf(): Promise<SelfDeclaration>;
  /** GET /api/v1/presence?userIds=a,b,c — batched, never one request per avatar. */
  fetchMany(userIds: readonly string[]): Promise<PresenceSnapshot[]>;
  /** PUT /api/v1/presence/me/state */
  setManualState(state: ManualState | null, resetAfter: ResetAfter): Promise<void>;
  /** PUT /api/v1/presence/me/status */
  setCustomStatus(status: CustomStatus | null): Promise<void>;
  /** POST /api/v1/presence/me/heartbeat — refreshes the 45s Redis TTL. */
  heartbeat(autoState: PresenceWire): Promise<void>;
  /**
   * WS `presence` topic. Returns an unsubscribe. Deltas arrive batched (250ms).
   *
   * THE CONTRACT INCLUDES THE COLD READ. An implementation must deliver the
   * CURRENT state of `userIds` as soon as the subscription exists, and again
   * whenever the transport reconnects or the window is woken — never only the
   * changes. A subscription that carries changes alone is wrong for as long as
   * the interval between them, which for an idle workspace is minutes.
   */
  subscribe(userIds: readonly string[], onDelta: (rows: PresenceSnapshot[]) => void): () => void;
}

const ms = (iso: string | null): number | null => (iso ? Date.parse(iso) : null);

export function presenceFromDto(dto: PresenceDto): PresenceSnapshot {
  const status: CustomStatus | null =
    dto.statusText || dto.statusEmoji
      ? {
          emoji: dto.statusEmoji,
          text: dto.statusText ?? '',
          expiresAt: ms(dto.statusExpiresAt),
          pinInChat: dto.statusPinInChat,
        }
      : null;

  return {
    userId: dto.userId,
    autoState: fromWire(dto.autoState),
    activityState: activityFromWire(dto.activityState),
    manualState: null,
    manualExpiresAt: ms(dto.manualExpiresAt),
    effectiveState: fromWire(dto.effectiveState),
    stateSince: ms(dto.stateSince) ?? Date.now(),
    lastActivityAt: ms(dto.lastActivityAt),
    connectionCount: dto.connectionCount,
    status,
    ooo: null,
    resolved: true,
  };
}

export function emptySnapshot(userId: string, now = Date.now()): PresenceSnapshot {
  return {
    userId,
    autoState: 'offline',
    /* Nothing has told us this person is doing anything. Not `available`. */
    activityState: null,
    manualState: null,
    manualExpiresAt: null,
    effectiveState: 'offline',
    stateSince: now,
    /* NOT `now`: nothing has told us when this person was last here, and
       stamping the current time makes every "last seen" count from the page
       load instead of from the person. */
    lastActivityAt: null,
    connectionCount: 0,
    status: null,
    ooo: null,
    /* Nothing has told us anything yet. */
    resolved: false,
  };
}
